/**
 * Cancellation policies — `/v1/admin/cancellation-policies*` (docs/05-admin/17 §2.5).
 *
 * A published version is never edited: bookings pin it. Changing a policy means starting the next
 * version as a draft copy, replacing the draft's windows, and publishing — which retires the
 * version it replaces while every booking that pinned the old one keeps it. Finance writes;
 * support reads. Every write is audited server-side as `cancellation_policy.*`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type PolicyVersionRow = components['schemas']['PolicyVersionRow'];
export type PolicyVersionDetail = components['schemas']['PolicyVersionDetail'];
export type PolicyWindow = components['schemas']['PolicyWindow-Output'];
/** What a replace sends; the fraction goes as a decimal string so 0.3333 is not float-rounded. */
export type PolicyWindowIn = components['schemas']['PolicyWindow-Input'];

export function useCancellationPolicies() {
  return useQuery({
    queryKey: keys.cancellationPolicies.all(),
    queryFn: () => authedClient.request<PolicyVersionRow[]>('/v1/admin/cancellation-policies'),
  });
}

export function useCancellationPolicy(id: number) {
  return useQuery({
    queryKey: keys.cancellationPolicies.one(id),
    queryFn: () => authedClient.request<PolicyVersionDetail>(`/v1/admin/cancellation-policies/${id}`),
  });
}

/** Every write changes the list (status, rule count) as well as the version, so both refresh. */
function usePolicyWrite<V>(call: (v: V) => Promise<PolicyVersionDetail | void>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: call,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.cancellationPolicies.all() }),
  });
}

export const useDraftPolicy = () =>
  usePolicyWrite(({ id }: { id: number }) =>
    authedClient.request<PolicyVersionDetail>(`/v1/admin/cancellation-policies/${id}/draft`, { method: 'POST' }),
  );

export const useReplacePolicyRules = () =>
  usePolicyWrite(({ id, rules }: { id: number; rules: PolicyWindowIn[] }) =>
    authedClient.request<PolicyVersionDetail>(`/v1/admin/cancellation-policies/${id}/rules`, {
      method: 'PUT',
      body: { rules },
    }),
  );

export const usePublishPolicy = () =>
  usePolicyWrite(({ id, reason }: { id: number; reason: string }) =>
    authedClient.request<PolicyVersionDetail>(`/v1/admin/cancellation-policies/${id}/publish`, {
      method: 'POST',
      body: { reason },
    }),
  );

export const useDiscardPolicy = () =>
  usePolicyWrite(({ id }: { id: number }) =>
    authedClient.request<void>(`/v1/admin/cancellation-policies/${id}`, { method: 'DELETE' }),
  );
